import React, { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Search, User, Heart, ShoppingBag, Menu, X, ChevronRight } from "lucide-react";
import { useStore } from "../../context/StoreContext";
import api, { img } from "../../lib/api";

const MAIN_LINKS = [
  { label: "Home", to: "/" },
  { label: "Shop", to: "/shop", mega: true },
  { label: "Our Story", to: "/our-story" },
  { label: "Journal", to: "/journal" },
  { label: "Rituals", to: "/rituals" },
  { label: "Contact", to: "/contact" },
];

const MEGA = [
  { title: "Face", to: "/collections/face", items: [["Cleansers", "face"], ["Scrubs", "face"], ["Serums & Elixirs", "face"], ["Moisturisers", "face"], ["Sunscreen", "face"]] },
  { title: "Hair", to: "/collections/hair", items: [["Shampoo", "hair"], ["Conditioner", "hair"], ["Hair Treatments", "hair"]] },
  { title: "Body", to: "/collections/body", items: [["Body Wash", "body"], ["Body Care", "body"]] },
  { title: "Fragrance", to: "/collections/fragrance", items: [["Eau de Parfum", "fragrance"], ["All Fragrance", "fragrance"]] },
  { title: "Collections", to: "/shop", items: [["Gift Sets", "gift-sets"], ["Best Sellers", "best-sellers"], ["New Arrivals", "new-arrivals"], ["The Glow Collection", "glow-collection"], ["The Rice Collection", "rice-collection"]] },
];

export default function Header() {
  const { settings, user, cartCount, wishlist, setCartOpen } = useStore();
  const [scrolled, setScrolled] = useState(false);
  const [mega, setMega] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [q, setQ] = useState("");
  const [results, setResults] = useState(null);
  const nav = useNavigate();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    if (!q.trim()) { setResults(null); return; }
    const t = setTimeout(() => {
      api.get(`/search?q=${encodeURIComponent(q)}`).then(r => setResults(r.data)).catch(() => {});
    }, 250);
    return () => clearTimeout(t);
  }, [q]);

  const submitSearch = (e) => {
    e.preventDefault();
    if (q.trim()) { setSearchOpen(false); nav(`/search?q=${encodeURIComponent(q)}`); }
  };

  return (
    <>
      {settings.announcement_enabled && (
        <div className="bg-mint text-forest text-center text-[11px] tracking-[0.18em] uppercase py-2.5 px-4 font-medium">
          {settings.announcement_bar}
        </div>
      )}
      <header className={`sticky top-0 z-40 border-b transition-colors duration-300 ${scrolled ? "bg-ivory/90 backdrop-blur-xl border-sage/30" : "bg-ivory border-transparent"}`}
        onMouseLeave={() => setMega(false)}>
        <div className="max-w-[1400px] mx-auto px-5 lg:px-10">
          <div className="h-16 lg:h-20 grid grid-cols-[1fr_auto_1fr] items-center">
            {/* left / mobile */}
            <div className="flex items-center gap-6">
              <button className="lg:hidden" onClick={() => setMobileOpen(true)} data-testid="mobile-menu-open" aria-label="Menu">
                <Menu size={22} strokeWidth={1.4} />
              </button>
              <nav className="hidden lg:flex items-center gap-7">
                {MAIN_LINKS.slice(0, 3).map((l) => (
                  <Link key={l.label} to={l.to} data-testid={`nav-${l.label.toLowerCase().replace(" ", "-")}`}
                    onMouseEnter={() => setMega(!!l.mega)}
                    className="text-xs uppercase tracking-[0.14em] text-forest/80 hover:text-forest link-underline">
                    {l.label}
                  </Link>
                ))}
              </nav>
            </div>
            {/* logo */}
            <Link to="/" className="flex flex-col items-center justify-center" data-testid="logo-home">
              {settings.logo_light
                ? <img src={img(settings.logo_light)} alt="the IAH" className="h-10 lg:h-[52px] w-auto" />
                : <div className="serif text-3xl lg:text-4xl tracking-[0.15em] leading-none text-forest">IAH</div>}
            </Link>
            {/* right */}
            <div className="flex items-center justify-end gap-5">
              <nav className="hidden lg:flex items-center gap-7 mr-2">
                {MAIN_LINKS.slice(3).map((l) => (
                  <Link key={l.label} to={l.to} onMouseEnter={() => setMega(false)}
                    data-testid={`nav-${l.label.toLowerCase().replace(" ", "-")}`}
                    className="text-xs uppercase tracking-[0.14em] text-forest/80 hover:text-forest link-underline">{l.label}</Link>
                ))}
              </nav>
              <button onClick={() => setSearchOpen(true)} data-testid="search-open" aria-label="Search"><Search size={19} strokeWidth={1.4} /></button>
              <Link to={user ? "/account" : "/login"} data-testid="account-link" aria-label="Account" className="hidden sm:block"><User size={19} strokeWidth={1.4} /></Link>
              <Link to="/wishlist" data-testid="wishlist-link" aria-label="Wishlist" className="relative hidden sm:block">
                <Heart size={19} strokeWidth={1.4} />
                {wishlist.length > 0 && <span className="absolute -top-2 -right-2 bg-forest text-ivory text-[9px] h-4 w-4 flex items-center justify-center rounded-full">{wishlist.length}</span>}
              </Link>
              <button onClick={() => setCartOpen(true)} data-testid="cart-open" aria-label="Cart" className="relative">
                <ShoppingBag size={19} strokeWidth={1.4} />
                {cartCount > 0 && <span className="absolute -top-2 -right-2 bg-forest text-ivory text-[9px] h-4 w-4 flex items-center justify-center rounded-full" data-testid="cart-count">{cartCount}</span>}
              </button>
            </div>
          </div>
        </div>

        {/* Mega menu */}
        {mega && (
          <div className="hidden lg:block absolute left-0 right-0 top-full bg-ivory border-b border-sage/30 shadow-[0_20px_40px_rgba(38,61,43,0.06)]"
            onMouseEnter={() => setMega(true)}>
            <div className="max-w-[1400px] mx-auto px-10 py-10 grid grid-cols-4 gap-10">
              {MEGA.map((col) => (
                <div key={col.title}>
                  <Link to={col.to} className="serif text-2xl text-forest mb-4 block">{col.title}</Link>
                  <ul className="space-y-2.5">
                    {col.items.map(([label, slug]) => (
                      <li key={label}>
                        <Link to={`/collections/${slug}`} className="text-sm text-forest/60 hover:text-forest transition-colors">{label}</Link>
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          </div>
        )}
      </header>

      {/* Search overlay */}
      {searchOpen && (
        <div className="fixed inset-0 z-[60] bg-forest/40 backdrop-blur-sm" onClick={() => setSearchOpen(false)}>
          <div className="bg-ivory p-6 lg:p-10" onClick={(e) => e.stopPropagation()}>
            <div className="max-w-3xl mx-auto">
              <form onSubmit={submitSearch} className="flex items-center gap-4 border-b border-forest/25 pb-3">
                <Search size={22} strokeWidth={1.4} className="text-forest/50" />
                <input autoFocus value={q} onChange={(e) => setQ(e.target.value)} data-testid="search-input"
                  placeholder="Search products, collections, journal…" className="flex-1 bg-transparent outline-none text-lg placeholder:text-forest/35" />
                <button type="button" onClick={() => setSearchOpen(false)} data-testid="search-close"><X size={22} strokeWidth={1.4} /></button>
              </form>
              {results && (
                <div className="mt-6 max-h-[60vh] overflow-auto hide-scrollbar">
                  {results.products?.length ? results.products.slice(0, 6).map((p) => (
                    <Link key={p.slug} to={`/product/${p.slug}`} onClick={() => setSearchOpen(false)}
                      className="flex items-center gap-4 py-3 border-b border-sage/20 hover:opacity-70">
                      <img src={img((p.images || [])[1] || (p.images || [])[0])} alt="" className="h-14 w-14 object-cover bg-beige/40" />
                      <div><div className="serif text-lg text-forest">{p.name}</div><div className="text-xs text-forest/50">{p.subtitle}</div></div>
                    </Link>
                  )) : <div className="serif text-2xl text-forest/50 py-8">Nothing found in this forest.</div>}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Mobile drawer */}
      {mobileOpen && (
        <div className="fixed inset-0 z-[60] lg:hidden">
          <div className="absolute inset-0 bg-forest/40" onClick={() => setMobileOpen(false)} />
          <div className="absolute left-0 top-0 bottom-0 w-[82%] max-w-sm bg-ivory p-6 overflow-auto">
            <div className="flex items-center justify-between mb-8">
              <span className="serif text-3xl tracking-[0.15em]">IAH</span>
              <button onClick={() => setMobileOpen(false)} data-testid="mobile-menu-close"><X size={24} strokeWidth={1.4} /></button>
            </div>
            <nav className="flex flex-col">
              {MAIN_LINKS.map((l) => (
                <Link key={l.label} to={l.to} onClick={() => setMobileOpen(false)}
                  className="flex items-center justify-between py-4 border-b border-sage/20 serif text-2xl text-forest">
                  {l.label} <ChevronRight size={18} className="text-forest/40" />
                </Link>
              ))}
            </nav>
            <div className="mt-8 flex flex-col gap-3">
              <Link to={user ? "/account" : "/login"} onClick={() => setMobileOpen(false)} className="text-sm uppercase tracking-[0.14em] text-forest/70">{user ? "My Account" : "Login / Register"}</Link>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
